import React from "react";

class TotalBalanceExchangeRateBase extends React.Component {
    constructor(props) {
        super(props);
        this.flashTimers = {};
        this.state = {
            flashingCards: {},
        };
    }

    componentDidUpdate(prevProps) {
        const prevCards = this._buildCards(prevProps);
        const currCards = this._buildCards(this.props);
        const nextFlashing = {};
        let hasChange = false;

        currCards.forEach((card, i) => {
            const prevCard = prevCards[i];
            if (prevCard && card.rawValue !== prevCard.rawValue) {
                const direction = card.rawValue > prevCard.rawValue ? 'gain' : 'loss';
                nextFlashing[card.label] = direction;
                hasChange = true;

                clearTimeout(this.flashTimers[card.label]);
                this.flashTimers[card.label] = setTimeout(() => {
                    this.setState((prev) => {
                        const updated = { ...prev.flashingCards };
                        delete updated[card.label];
                        return { flashingCards: updated };
                    });
                }, 600);
            }
        });

        if (hasChange) {
            this.setState((prev) => ({
                flashingCards: { ...prev.flashingCards, ...nextFlashing },
            }));
        }
    }

    componentWillUnmount() {
        Object.values(this.flashTimers).forEach(clearTimeout);
    }

    _buildCards = (props) => {
        const {
            fiatTotalBalance,
            fiatTotalUnrealizedProfit,
            riskyTotalUnrealizedProfit,
            riskyTotalMarginBalance,
            btcSpotBalance,
            exchangeRate,
        } = props;
        const profitAndLoss = exchangeRate * riskyTotalUnrealizedProfit + fiatTotalUnrealizedProfit;
        const totalRiskyBalance = exchangeRate * riskyTotalMarginBalance;
        const totalBalance = totalRiskyBalance + fiatTotalBalance;
        // 리밸런싱 계산은 보유 BTC 현물 평가액 제외 기준 (DAS-172) — 잔액 표시는 실보유 그대로
        const heldBtcValue = Math.max(0, Number(btcSpotBalance) || 0);
        const rebalanceBase = Math.max(0, totalBalance - heldBtcValue);
        const requiredFutures = rebalanceBase * 0.25;
        const sendBalance = Math.abs(requiredFutures - totalRiskyBalance);
        return [
            { label: "잔고 총액 (KRW)", rawValue: totalBalance },
            { label: "Fiat 총액 (KRW)", rawValue: fiatTotalBalance },
            { label: "Risky 총액 (KRW)", rawValue: totalRiskyBalance },
            { label: "필요 Risky 잔액", rawValue: requiredFutures },
            { label: "보내야할 금액", rawValue: sendBalance },
            { label: "받는 곳", rawValue: 0 },
            { label: "미실현 손익 총액", rawValue: profitAndLoss },
            { label: "Fiat 미실현 손익", rawValue: fiatTotalUnrealizedProfit },
            { label: "Risky 미실현 손익", rawValue: exchangeRate * riskyTotalUnrealizedProfit },
        ];
    };

    formatNumberWithCommaAndDecimal = (number, decimalPlaces = 3) => {
        if (isNaN(number)) return "";
        return Number(number).toLocaleString(undefined, {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
        });
    };

    render() {
        const {
            fiatTotalBalance,
            fiatTotalUnrealizedProfit,
            riskyTotalUnrealizedProfit,
            riskyTotalMarginBalance,
            btcSpotBalance,
            exchangeRate,
        } = this.props;

        const profitAndLoss = exchangeRate * riskyTotalUnrealizedProfit + fiatTotalUnrealizedProfit;
        const totalRiskyBalance = exchangeRate * riskyTotalMarginBalance;
        const totalBalance = totalRiskyBalance + fiatTotalBalance;
        // 리밸런싱 계산은 보유 BTC 현물 평가액 제외 기준 (DAS-172) — _buildCards와 동일 규칙
        const heldBtcValue = Math.max(0, Number(btcSpotBalance) || 0);
        const rebalanceBase = Math.max(0, totalBalance - heldBtcValue);
        const requiredFutures = rebalanceBase * 0.25;
        const sendBalance = Math.abs(requiredFutures - totalRiskyBalance);
        const receiveExchange =
            rebalanceBase - requiredFutures < fiatTotalBalance - heldBtcValue ? "Risky" : "Fiat";

        const cards = [
            {
                label: "잔고 총액 (KRW)",
                value: `₩${this.formatNumberWithCommaAndDecimal(totalBalance, 0)}`,
            },
            {
                label: "Fiat 총액 (KRW)",
                value: `₩${this.formatNumberWithCommaAndDecimal(fiatTotalBalance, 0)}`,
            },
            {
                label: "Risky 총액 (KRW)",
                value: `₩${this.formatNumberWithCommaAndDecimal(totalRiskyBalance, 0)}`,
            },
            {
                label: "필요 Risky 잔액",
                value: `₩${this.formatNumberWithCommaAndDecimal(requiredFutures, 0)}`,
            },
            {
                label: "보내야할 금액",
                value: `₩${this.formatNumberWithCommaAndDecimal(sendBalance, 0)}`,
            },
            {
                label: "받는 곳",
                value: receiveExchange,
            },
            {
                label: "미실현 손익 총액",
                value: `₩${this.formatNumberWithCommaAndDecimal(profitAndLoss, 0)}`,
                colored: true,
                amount: profitAndLoss,
            },
            {
                label: "Fiat 미실현 손익",
                value: `₩${this.formatNumberWithCommaAndDecimal(fiatTotalUnrealizedProfit, 0)}`,
                colored: true,
                amount: fiatTotalUnrealizedProfit,
            },
            {
                label: "Risky 미실현 손익",
                value: `₩${this.formatNumberWithCommaAndDecimal(exchangeRate * riskyTotalUnrealizedProfit, 0)}`,
                colored: true,
                amount: exchangeRate * riskyTotalUnrealizedProfit,
            },
        ];

        const { flashingCards } = this.state;

        return (
            <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-3">
                {cards.map((card, index) => {
                    const flashDir = flashingCards[card.label];
                    const flashClass = flashDir === 'gain' ? 'animate-flash-gain' : flashDir === 'loss' ? 'animate-flash-loss' : '';
                    return (
                    <div
                        key={card.label}
                        className={`min-w-0 overflow-hidden rounded-xl border border-surface-border bg-surface-elevated p-3 sm:p-4 ${
                            index === 0 ? "col-span-2 lg:col-span-3" : ""
                        } ${flashClass}`}
                    >
                        <div className="text-xs font-semibold uppercase tracking-wider text-content-secondary">
                            {card.label}
                        </div>
                        <div className={`mt-1.5 truncate font-num font-semibold sm:mt-2 ${
                            index === 0
                                ? "text-base text-content sm:text-xl"
                                : card.colored
                                    ? card.amount >= 0 ? "text-sm text-gain sm:text-lg" : "text-sm text-loss sm:text-lg"
                                    : "text-sm text-content sm:text-lg"
                        }`}>
                            {card.colored && Number.isFinite(card.amount) && card.amount !== 0 && (
                                <svg
                                    className={`inline-block h-2.5 w-2.5 mr-0.5${card.amount < 0 ? " rotate-180" : ""}`}
                                    viewBox="0 0 12 12"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path d="M6 2l4 5H2z" />
                                </svg>
                            )}
                            {card.value}
                        </div>
                    </div>
                    );
                })}
            </div>
        );
    }
}

export default TotalBalanceExchangeRateBase;
